import { z } from 'zod';

/**
 * Emoji rating scale: 1-5
 * 1 = 😡 (Hate it)
 * 2 = 😕 (Dislike it)
 * 3 = 😐 (Neutral)
 * 4 = 😊 (Like it)
 * 5 = 😍 (Love it)
 */
export const EmojiRatingSchema = z.number().int().min(1).max(5);

export type EmojiRating = z.infer<typeof EmojiRatingSchema>;

/**
 * Feature definition - describes a feature released in a sprint
 */
export const FeatureDefinitionSchema = z.object({
  id: z.string(), // "v2.1-chat-archive"
  name: z.string(), // "Chat Archive & Delete"
  description: z.string(), // Short description
  version: z.string(), // "2.1.0"
  releaseDate: z.string(), // ISO date
  category: z.string().optional(), // "Chat", "Billing", "AI", etc.
});

export type FeatureDefinition = z.infer<typeof FeatureDefinitionSchema>;

/**
 * User feedback for a specific feature
 */
export const UserFeedbackSchema = z.object({
  featureId: z.string(),
  rating: EmojiRatingSchema,
  comment: z.string().optional(),
  submittedAt: z.string(), // ISO timestamp
  appVersion: z.string(), // Version when feedback was submitted
});

export type UserFeedback = z.infer<typeof UserFeedbackSchema>;

/**
 * Complete feedback submission payload
 */
export const SubmitFeedbackInputSchema = z.object({
  featureId: z.string(),
  rating: EmojiRatingSchema,
  comment: z.string().optional(),
});

export type SubmitFeedbackInput = z.infer<typeof SubmitFeedbackInputSchema>;

/**
 * Feedback submission response
 */
export const SubmitFeedbackOutputSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

export type SubmitFeedbackOutput = z.infer<typeof SubmitFeedbackOutputSchema>;

/**
 * Get features response - includes features and user's feedback status
 */
export const GetFeaturesOutputSchema = z.object({
  features: z.array(FeatureDefinitionSchema),
  userFeedback: z.array(UserFeedbackSchema),
});

export type GetFeaturesOutput = z.infer<typeof GetFeaturesOutputSchema>;

/**
 * Dismiss features (user clicked "Remind me later")
 */
export const DismissFeaturesInputSchema = z.object({
  featureIds: z.array(z.string()),
});

export type DismissFeaturesInput = z.infer<typeof DismissFeaturesInputSchema>;

/**
 * Client-side feedback state (stored in Zustand + localStorage)
 */
export type FeedbackState = {
  clickCount: number; // Total clicks since install
  lastShownVersion: string | null; // Last version popup was shown for
  lastDismissedAt: string | null; // When user clicked "Remind me later"
  dismissClickThreshold: number; // How many clicks needed after dismiss
  hasRatedCurrentVersion: boolean; // Whether user rated features in current version
};

/**
 * Emoji rating display info
 */
export type EmojiRatingInfo = {
  emoji: string;
  label: string;
  value: EmojiRating;
};

export const EMOJI_RATINGS: EmojiRatingInfo[] = [
  { emoji: '😡', label: 'Hate it', value: 1 },
  { emoji: '😕', label: 'Dislike it', value: 2 },
  { emoji: '😐', label: 'Neutral', value: 3 },
  { emoji: '😊', label: 'Like it', value: 4 },
  { emoji: '😍', label: 'Love it', value: 5 },
];
